/**
 * Runtime display-only derivation for BrandRating inactive (`ratings.icon`) rasters.
 *
 * Purpose:
 *   Uploaded inactive PNG artwork often includes a soft white outer glow that
 *   reads cleanly at full size but becomes a white fringe/halo at icon size
 *   (~56px). This module builds a derived blob URL used only by the inactive
 *   layer at display time.
 *
 * Source preservation:
 *   - Does NOT modify Brand Asset Manager storage, uploads, or the original URL.
 *   - Does NOT touch `ratings.active` bytes / storage.
 *   - Original PNG bytes remain the canonical stored asset.
 *
 * Algorithm (canvas, per-pixel):
 *   1. Decode the inactive source into an offscreen canvas (natural size).
 *   2. For each pixel, detect near-white / low-chroma fringe (baked soft-matte
 *      glow): high RGB, low saturation.
 *   3. Suppress only those fringe pixels by reducing alpha (stronger as the
 *      pixel approaches pure white). Gray dove body and soft-alpha gray art
 *      are left unchanged — this is intentionally not an alpha-paint rematte.
 *   4. When an active reference raster is provided, measure solid-core content
 *      bounds (high alpha) on both images and reframe the halo-stripped
 *      inactive so its solid core occupies the same rect as the active solid
 *      core inside a canvas that matches the active intrinsic size. Soft edges
 *      around the core are scaled proportionally so object-fit:contain yields
 *      an identical visual footprint — only color/fill differs.
 *      (Soft alpha≥12 AABBs alone are wrong for gold assets: glow/particles
 *      inflate active bounds so the solid dove reads smaller than inactive.)
 *   5. Encode PNG → blob URL for the inactive <img> src.
 *
 * Caching: one derived blob URL per (source, reference) pair; callers may
 * revoke via revokeDerivedInactiveRatingDisplay when the source changes.
 */

var cache = Object.create(null);
var inflight = Object.create(null);

/** Soft outer edge (glow / AA) — used only to preserve fringe when reframing. */
var EDGE_ALPHA = 12;
/**
 * Solid body — gold glow/particles sit below this; gray inactive body is above.
 * Matching cores (not soft AABBs) keeps select/deselect footprint identical.
 */
var SOLID_ALPHA = 128;

function isRasterIconUrl(url) {
  var path = String(url || "")
    .split("?")[0]
    .split("#")[0]
    .toLowerCase();
  return /\.(png|webp|jpe?g)$/.test(path);
}

function cacheKey(sourceUrl, referenceUrl) {
  return String(sourceUrl || "") + "\0" + String(referenceUrl || "") + "\0v2-solid";
}

/**
 * Near-white fringe: high luminance + low chroma (glow / soft-matte),
 * not mid-gray dove paint (~#B8B8B8).
 */
function whiteFringeFactor(r, g, b) {
  var max = Math.max(r, g, b);
  var min = Math.min(r, g, b);
  if (max < 210) return 0;
  var chroma = max - min;
  if (chroma > 36) return 0;
  // Mid-gray body stays below this band; fringe climbs toward 255.
  if (min < 195) return 0;
  // 0 at soft threshold → 1 at near-pure white.
  var whiteness = (min - 195) / (255 - 195);
  if (whiteness < 0) whiteness = 0;
  if (whiteness > 1) whiteness = 1;
  // Extra weight when very bright (halo cores at icon size).
  var bright = (max - 210) / (255 - 210);
  if (bright < 0) bright = 0;
  if (bright > 1) bright = 1;
  return Math.max(whiteness, bright * 0.85);
}

function processImageData(imageData) {
  var data = imageData.data;
  for (var i = 0; i < data.length; i += 4) {
    var a = data[i + 3];
    if (a === 0) continue;
    var factor = whiteFringeFactor(data[i], data[i + 1], data[i + 2]);
    if (factor <= 0) continue;
    // Kill fringe alpha; leave RGB of non-fringe art untouched.
    var nextA = Math.round(a * (1 - factor));
    data[i + 3] = nextA < 8 && factor > 0.55 ? 0 : nextA;
  }
  return imageData;
}

/**
 * Axis-aligned bounds of pixels with alpha above threshold.
 * @returns {{ x: number, y: number, w: number, h: number } | null}
 */
function opaqueContentBounds(imageData, minAlpha) {
  var threshold = minAlpha == null ? EDGE_ALPHA : minAlpha;
  var data = imageData.data;
  var width = imageData.width;
  var height = imageData.height;
  var minX = width;
  var minY = height;
  var maxX = -1;
  var maxY = -1;
  for (var y = 0; y < height; y++) {
    for (var x = 0; x < width; x++) {
      if (data[(y * width + x) * 4 + 3] < threshold) continue;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }
  if (maxX < 0) return null;
  return {
    x: minX,
    y: minY,
    w: maxX - minX + 1,
    h: maxY - minY + 1,
  };
}

function clampBounds(bounds, canvasW, canvasH) {
  if (!bounds) return null;
  var x = Math.max(0, Math.floor(bounds.x));
  var y = Math.max(0, Math.floor(bounds.y));
  var right = Math.min(canvasW, Math.ceil(bounds.x + bounds.w));
  var bottom = Math.min(canvasH, Math.ceil(bounds.y + bounds.h));
  var w = right - x;
  var h = bottom - y;
  if (w <= 0 || h <= 0) return null;
  return { x: x, y: y, w: w, h: h };
}

/**
 * Reframe inactive so its solid core matches the active solid core on a canvas
 * matching the active intrinsic size. Soft edges outside the solid core are
 * included and scaled with the same transform so glow/AA is not clipped.
 */
function reframeToReferenceSolidCore(
  srcCanvas,
  srcSoft,
  srcSolid,
  destW,
  destH,
  destSolid
) {
  var out = document.createElement("canvas");
  out.width = destW;
  out.height = destH;
  var ctx = out.getContext("2d");
  if (!ctx || !srcSoft || !srcSolid || !destSolid) {
    return srcCanvas;
  }
  if (srcSolid.w <= 0 || srcSolid.h <= 0 || destSolid.w <= 0 || destSolid.h <= 0) {
    return srcCanvas;
  }

  var scaleX = destSolid.w / srcSolid.w;
  var scaleY = destSolid.h / srcSolid.h;
  // Map soft region with the solid-core transform so cores coincide exactly.
  var destX = destSolid.x + (srcSoft.x - srcSolid.x) * scaleX;
  var destY = destSolid.y + (srcSoft.y - srcSolid.y) * scaleY;
  var destDrawW = srcSoft.w * scaleX;
  var destDrawH = srcSoft.h * scaleY;

  var srcDraw = clampBounds(srcSoft, srcCanvas.width, srcCanvas.height);
  var destDraw = clampBounds(
    { x: destX, y: destY, w: destDrawW, h: destDrawH },
    destW,
    destH
  );
  if (!srcDraw || !destDraw) {
    return srcCanvas;
  }

  ctx.clearRect(0, 0, destW, destH);
  // Prefer high-quality downscale for large BAM uploads → icon size.
  if ("imageSmoothingEnabled" in ctx) {
    ctx.imageSmoothingEnabled = true;
  }
  if ("imageSmoothingQuality" in ctx) {
    ctx.imageSmoothingQuality = "high";
  }
  ctx.drawImage(
    srcCanvas,
    srcDraw.x,
    srcDraw.y,
    srcDraw.w,
    srcDraw.h,
    destDraw.x,
    destDraw.y,
    destDraw.w,
    destDraw.h
  );
  return out;
}

function canvasToBlobUrl(canvas) {
  return new Promise(function (resolve, reject) {
    if (canvas.toBlob) {
      canvas.toBlob(
        function (blob) {
          if (!blob) {
            reject(new Error("deriveInactiveRatingDisplay: toBlob failed"));
            return;
          }
          resolve(URL.createObjectURL(blob));
        },
        "image/png"
      );
      return;
    }
    try {
      var dataUrl = canvas.toDataURL("image/png");
      resolve(dataUrl);
    } catch (err) {
      reject(err);
    }
  });
}

function loadImage(url) {
  return new Promise(function (resolve, reject) {
    var img = new Image();
    img.decoding = "async";
    // Cross-origin assets need CORS for canvas read; BAM URLs are typically
    // same-origin or CORS-enabled CDN. Failure falls back to original src.
    img.crossOrigin = "anonymous";
    img.onload = function () {
      resolve(img);
    };
    img.onerror = function () {
      reject(new Error("deriveInactiveRatingDisplay: image load failed"));
    };
    img.src = url;
  });
}

function drawImageToCanvas(img) {
  var w = img.naturalWidth || img.width;
  var h = img.naturalHeight || img.height;
  if (!w || !h) return null;
  var canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  var ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.clearRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0, w, h);
  return canvas;
}

function readImageData(canvas) {
  var ctx = canvas.getContext("2d");
  if (!ctx) return null;
  try {
    return ctx.getImageData(0, 0, canvas.width, canvas.height);
  } catch (err) {
    // Tainted canvas (CORS).
    return null;
  }
}

/**
 * @param {string} sourceUrl — original BAM / override inactive icon URL
 * @param {string} [referenceUrl] — active icon URL for content-bounds matching
 * @returns {Promise<string>} derived blob URL, or sourceUrl when derivation
 *   is skipped / fails (SVG, non-raster, CORS, etc.)
 */
export function deriveInactiveRatingDisplay(sourceUrl, referenceUrl) {
  var url = String(sourceUrl || "").trim();
  var ref = String(referenceUrl || "").trim();
  if (!url) {
    return Promise.resolve("");
  }
  if (!isRasterIconUrl(url)) {
    return Promise.resolve(url);
  }
  // Only use reference for framing when it is a distinct raster.
  if (!ref || ref === url || !isRasterIconUrl(ref)) {
    ref = "";
  }
  var key = cacheKey(url, ref);
  if (cache[key]) {
    return Promise.resolve(cache[key]);
  }
  if (inflight[key]) {
    return inflight[key];
  }

  inflight[key] = loadImage(url)
    .then(function (img) {
      var canvas = drawImageToCanvas(img);
      if (!canvas) {
        return url;
      }
      var imageData = readImageData(canvas);
      if (!imageData) {
        return url;
      }
      processImageData(imageData);
      var ctx = canvas.getContext("2d");
      ctx.putImageData(imageData, 0, 0);

      if (!ref) {
        return canvasToBlobUrl(canvas);
      }

      return loadImage(ref)
        .then(function (refImg) {
          var refCanvas = drawImageToCanvas(refImg);
          if (!refCanvas) {
            return canvasToBlobUrl(canvas);
          }
          var refData = readImageData(refCanvas);
          if (!refData) {
            return canvasToBlobUrl(canvas);
          }
          var srcSoft = opaqueContentBounds(imageData, EDGE_ALPHA);
          var srcSolid = opaqueContentBounds(imageData, SOLID_ALPHA);
          var destSolid = opaqueContentBounds(refData, SOLID_ALPHA);
          // Fall back to soft AABB match if solid cores cannot be measured.
          if (!srcSolid || !destSolid) {
            var destSoft = opaqueContentBounds(refData, EDGE_ALPHA);
            if (!srcSoft || !destSoft) {
              return canvasToBlobUrl(canvas);
            }
            srcSolid = srcSoft;
            destSolid = destSoft;
          }
          if (!srcSoft) {
            srcSoft = srcSolid;
          }
          var framed = reframeToReferenceSolidCore(
            canvas,
            srcSoft,
            srcSolid,
            refCanvas.width,
            refCanvas.height,
            destSolid
          );
          return canvasToBlobUrl(framed);
        })
        .catch(function () {
          // Reference framing failed — keep halo-stripped inactive as-is.
          return canvasToBlobUrl(canvas);
        });
    })
    .then(function (derived) {
      cache[key] = derived;
      delete inflight[key];
      return derived;
    })
    .catch(function () {
      delete inflight[key];
      // Fail open: show original uploaded asset.
      return url;
    });

  return inflight[key];
}

/**
 * Drop a cached derived URL. Prefer leaving session cache intact — multiple
 * BrandRating slots share one blob per source URL. Only revoke when explicitly
 * clearing a replaced source (rare); otherwise keep until page unload.
 */
export function revokeDerivedInactiveRatingDisplay(sourceUrl, referenceUrl) {
  var url = String(sourceUrl || "").trim();
  if (!url) return;
  var ref = String(referenceUrl || "").trim();
  if (!ref || ref === url || !isRasterIconUrl(ref)) {
    ref = "";
  }
  var key = cacheKey(url, ref);
  if (!cache[key]) return;
  var derived = cache[key];
  delete cache[key];
  if (
    derived &&
    derived !== url &&
    typeof derived === "string" &&
    derived.indexOf("blob:") === 0
  ) {
    try {
      URL.revokeObjectURL(derived);
    } catch (err) {
      // ignore
    }
  }
}

export { isRasterIconUrl, opaqueContentBounds, SOLID_ALPHA, EDGE_ALPHA };
export default deriveInactiveRatingDisplay;
